import { useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Select } from 'foundations-components/transitional/components/Select';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { View } from 'foundations-components/transitional/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { useInviteUserMutation, useListUserInvitationsQuery, useUninviteUserMutation, useChangeUserRoleMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessDelegationRpc.hooks';
import { canChangeRoles, canInviteUsers, canRemoveUsers, getAllSupportedRoles, getAssignableUserRoles, getDefaultUserRole, toDisplayRole } from '../permissions/sharePermissions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AVATAR_COLORS = ['fill-accent-orange-default', 'fill-accent-teal-default', 'fill-accent-purple-default', 'fill-accent-magenta-default', 'fill-accent-blue-default', 'fill-accent-green-default'];
function getAvatarColor(index) {
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
}
function getRedirectSlug() {
  const prefixTag = document.querySelector('meta[name="x-hs-portals-prefix"]');
  const rawPrefix = (prefixTag === null || prefixTag === void 0 ? void 0 : prefixTag.getAttribute('content')) || '';
  const prefixParts = rawPrefix.split('/').filter(Boolean);
  return prefixParts.length > 0 ? `${prefixParts.join('/')}/` : '/';
}
function formatName(user) {
  var _user$firstName, _user$lastName;
  const firstName = ((_user$firstName = user.firstName) === null || _user$firstName === void 0 ? void 0 : _user$firstName.trim()) || '';
  const lastName = ((_user$lastName = user.lastName) === null || _user$lastName === void 0 ? void 0 : _user$lastName.trim()) || '';
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }
  return user.email.split('@')[0];
}
function getInitials(value) {
  const parts = value.split(/[.\s_-]+/).map(part => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return value.substring(0, 2).toUpperCase();
}
function isInvitationsDisabledError(error) {
  var _err$rpcError;
  const err = error;
  return (err === null || err === void 0 || (_err$rpcError = err.rpcError) === null || _err$rpcError === void 0 ? void 0 : _err$rpcError.errorType) === 'INVITATIONS_DISABLED';
}
function getSafeErrorMessage(error, fallbackMessage) {
  if (isInvitationsDisabledError(error)) {
    return I18n.text('app.shareModal.errorInvitationsDisabled');
  }
  return fallbackMessage;
}
function Avatar({
  initials,
  color
}) {
  return /*#__PURE__*/_jsx(Flex, {
    backgroundColor: color,
    borderRadius: "full",
    inlineSize: "40px",
    blockSize: "40px",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    children: /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      color: "text-inverse-default",
      children: initials
    })
  });
}
export function ShareModal({
  open,
  onClose,
  portalInstanceId,
  accountId,
  portalUserRole
}) {
  const [emailInput, setEmailInput] = useState('');
  const [selectedInviteRole, setSelectedInviteRole] = useState(getDefaultUserRole());
  const [inviteErrorMessage, setInviteErrorMessage] = useState(null);
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);
  const {
    data: invitationData,
    loading: isLoadingInvitations,
    error: invitationError,
    refetch
  } = useListUserInvitationsQuery({
    variables: {
      portalInstanceId,
      accountId
    },
    fetchPolicy: 'cache-and-network'
  });
  const [inviteUser, {
    loading: isInvitingUser
  }] = useInviteUserMutation();
  const [uninviteUser, {
    loading: isUninviting
  }] = useUninviteUserMutation();
  const [changeUserRole] = useChangeUserRoleMutation();
  const redirectSlug = useMemo(() => getRedirectSlug(), []);
  const canInvite = canInviteUsers(portalUserRole);
  const canChange = canChangeRoles(portalUserRole);
  const canRemove = canRemoveUsers(portalUserRole);
  useEffect(() => {
    setSelectedInviteRole(currentRole => getAssignableUserRoles(portalUserRole).includes(currentRole) ? currentRole : getDefaultUserRole());
  }, [portalUserRole]);
  const teamMembers = useMemo(() => {
    const allUsers = (invitationData === null || invitationData === void 0 ? void 0 : invitationData.invitedUsers) || [];
    return allUsers.map((user, index) => {
      const name = formatName(user);
      return {
        id: String(user.id),
        invitationId: user.id,
        name,
        email: user.email,
        role: toDisplayRole(user.role),
        status: user.status,
        avatarInitials: getInitials(name),
        avatarColor: getAvatarColor(index)
      };
    });
  }, [invitationData]);
  const handleInvite = async () => {
    if (!canInvite) return;
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) return;
    setInviteErrorMessage(null);
    setInviteSuccessMessage(null);
    try {
      await inviteUser({
        variables: {
          invitedEmail: trimmedEmail,
          userRole: selectedInviteRole,
          redirectSlug,
          portalInstanceId,
          accountId
        }
      });
      setEmailInput('');
      setInviteSuccessMessage(I18n.text('app.shareModal.inviteSuccess', {
        email: trimmedEmail
      }));
      void refetch().catch(() => undefined);
    } catch (err) {
      setInviteErrorMessage(getSafeErrorMessage(err, I18n.text('app.shareModal.errorInviteGeneric')));
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      void handleInvite();
    }
  };
  const handleRoleChange = async (member, newRole) => {
    if (!canChange) return;
    if (newRole === member.role) return;
    setActionFeedback(null);
    try {
      await changeUserRole({
        variables: {
          id: member.invitationId,
          role: newRole,
          portalInstanceId,
          accountId
        }
      });
      setActionFeedback({
        type: 'success',
        message: I18n.text('app.shareModal.roleChangeSuccess', {
          name: member.name
        })
      });
      void refetch().catch(() => undefined);
    } catch (_unused) {
      setActionFeedback({
        type: 'danger',
        message: I18n.text('app.shareModal.roleChangeError')
      });
    }
  };
  const handleRemoveConfirm = async member => {
    if (!canRemove) return;
    setActionFeedback(null);
    try {
      await uninviteUser({
        variables: {
          id: member.invitationId,
          portalInstanceId,
          accountId
        }
      });
      setActionFeedback({
        type: 'success',
        message: I18n.text('app.shareModal.removeSuccess', {
          name: member.name
        })
      });
      setConfirmingRemoveId(null);
      void refetch().catch(() => undefined);
    } catch (_unused2) {
      setActionFeedback({
        type: 'danger',
        message: I18n.text('app.shareModal.removeError')
      });
      setConfirmingRemoveId(null);
    }
  };
  if (!open) return null;
  const inviteRoleOptions = getAssignableUserRoles(portalUserRole).map(role => ({
    text: role === 'EDITOR' ? I18n.text('app.shareModal.roleEditor') : I18n.text('app.shareModal.roleViewer'),
    value: role
  }));
  const canChooseInviteRole = inviteRoleOptions.length > 1;
  const roleOptions = getAllSupportedRoles().map(role => ({
    text: role === 'EDITOR' ? I18n.text('app.shareModal.roleEditor') : I18n.text('app.shareModal.roleViewer'),
    value: role
  }));
  const handleInviteRoleChange = event => {
    setSelectedInviteRole(toDisplayRole(event.target.value));
  };
  return /*#__PURE__*/_jsxs(Modal, {
    width: 560,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.shareModal.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      maxBlockSize: "60vh",
      overflow: "auto",
      children: [canInvite ? /*#__PURE__*/_jsxs(View, {
        marginBlockEnd: "500",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          columnGap: "200",
          children: [/*#__PURE__*/_jsx(View, {
            flexGrow: 1,
            children: /*#__PURE__*/_jsx(TextInput, {
              placeholder: I18n.text('app.shareModal.inputPlaceholder'),
              value: emailInput,
              onChange: e => setEmailInput(e.target.value),
              onKeyDown: handleKeyDown,
              disabled: isInvitingUser
            })
          }), /*#__PURE__*/_jsx(View, {
            minInlineSize: "120px",
            children: /*#__PURE__*/_jsx(Select, {
              value: selectedInviteRole,
              onChange: handleInviteRoleChange,
              options: inviteRoleOptions,
              disabled: isInvitingUser || !canChooseInviteRole
            })
          }), /*#__PURE__*/_jsx(LoadingButton, {
            use: "secondary",
            loading: isInvitingUser,
            onClick: () => void handleInvite(),
            disabled: isInvitingUser || !emailInput.trim().length,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.shareModal.invite"
            })
          })]
        }), inviteSuccessMessage ? /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(Alert, {
            type: "success",
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: inviteSuccessMessage
            })
          })
        }) : null, inviteErrorMessage ? /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(Alert, {
            type: "danger",
            children: /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: inviteErrorMessage
            })
          })
        }) : null]
      }) : null, /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(Paragraph, {
          typography: "heading-100",
          marginBlockEnd: "300",
          color: "text-core-default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.shareModal.peopleWithAccess"
          })
        }), isLoadingInvitations ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : invitationError ? /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: getSafeErrorMessage(invitationError, I18n.text('app.shareModal.errorLoadInvitedUsers'))
          })
        }) : teamMembers.length === 0 ? /*#__PURE__*/_jsx(Alert, {
          type: "info",
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            rowGap: "100",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-200",
              as: "strong",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.shareModal.emptyStateTitle"
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: "body-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.shareModal.emptyStateBody"
              })
            })]
          })
        }) : /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          rowGap: "100",
          children: teamMembers.map(member => /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            justifyContent: "space-between",
            paddingBlock: "200",
            children: [/*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              columnGap: "300",
              minInlineSize: "0",
              flexShrink: 1,
              children: [/*#__PURE__*/_jsx(Avatar, {
                initials: member.avatarInitials,
                color: member.avatarColor
              }), /*#__PURE__*/_jsxs(Flex, {
                flexDirection: "column",
                rowGap: "50",
                minInlineSize: "0",
                children: [/*#__PURE__*/_jsx(TruncateString, {
                  tooltip: member.name,
                  children: /*#__PURE__*/_jsx(Text, {
                    typography: "body-100",
                    color: "text-core-default",
                    children: member.name
                  })
                }), /*#__PURE__*/_jsx(TruncateString, {
                  tooltip: member.email,
                  children: /*#__PURE__*/_jsx(Text, {
                    typography: "detail-100",
                    color: "text-core-subtle",
                    children: member.email
                  })
                }), /*#__PURE__*/_jsx(Text, {
                  typography: "detail-100",
                  color: "text-core-subtle",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: member.status === 'REGISTERED' ? 'app.shareModal.statusRegistered' : 'app.shareModal.statusInvited'
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              columnGap: "200",
              children: canRemove && confirmingRemoveId === member.id ? /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(Button, {
                  use: "tertiary-light",
                  size: "extra-small",
                  onClick: () => setConfirmingRemoveId(null),
                  disabled: isUninviting,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.shareModal.removeCancel"
                  })
                }), /*#__PURE__*/_jsx(LoadingButton, {
                  use: "danger",
                  size: "extra-small",
                  loading: isUninviting,
                  onClick: () => void handleRemoveConfirm(member),
                  disabled: isUninviting,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.shareModal.removeConfirmButton"
                  })
                })]
              }) : /*#__PURE__*/_jsxs(_Fragment, {
                children: [canChange ? /*#__PURE__*/_jsx(Select, {
                  value: member.role,
                  buttonSize: "extra-small",
                  onChange: e => void handleRoleChange(member, toDisplayRole(e.target.value)),
                  options: roleOptions,
                  disabled: isUninviting
                }) : /*#__PURE__*/_jsx(Text, {
                  typography: "body-100",
                  color: "text-core-subtle",
                  children: member.role === 'VIEWER' ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.shareModal.roleViewer"
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "app.shareModal.roleEditor"
                  })
                }), canRemove ? /*#__PURE__*/_jsx(IconButton, {
                  use: "link",
                  size: "small",
                  tooltip: I18n.text('app.shareModal.removeAccess'),
                  "aria-label": I18n.text('app.shareModal.removeAccess'),
                  onClick: () => {
                    setActionFeedback(null);
                    setConfirmingRemoveId(member.id);
                  },
                  disabled: isUninviting,
                  children: /*#__PURE__*/_jsx(DeleteIcon, {})
                }) : null]
              })
            })]
          }, member.id))
        }), actionFeedback ? /*#__PURE__*/_jsx(View, {
          marginBlockStart: "300",
          children: /*#__PURE__*/_jsx(Alert, {
            type: actionFeedback.type,
            children: actionFeedback.message
          })
        }) : null]
      })]
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "space-between",
        alignItems: "center",
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.shareModal.done"
          })
        })
      })
    })]
  });
}